import { useEffect, useState, type ReactNode } from 'react';
import { adminApi, type AdminProposalDetail, type AdminViewsMeta } from '../../lib/api';
import { relativeTime } from '../../lib/format';
import LifecycleActions from './LifecycleActions';
import { SectionCard } from './fields';

export default function ProposalManage({ proposal, flushPending, onChanged, onVersions, children }: {
  proposal: AdminProposalDetail; flushPending: () => Promise<boolean>; onChanged: () => Promise<void>;
  onVersions: () => void; children?: ReactNode;
}) {
  const [activity, setActivity] = useState<AdminViewsMeta | null>(null);
  const [activityError, setActivityError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [notice, setNotice] = useState('');
  const [sharing, setSharing] = useState(false);
  const url = new URL(`/${proposal.slug}/`, location.origin).href;
  const live = proposal.status === 'published' || proposal.status === 'signed';
  useEffect(() => {
    let cancelled = false;
    setActivityError('');
    setActivity(null);
    adminApi.getViews(proposal.id).then(result => {
      if (!cancelled) setActivity(result.meta ?? { open_count: 0, first_viewed_at: null, last_viewed_at: null });
    }).catch(() => { if (!cancelled) setActivityError('Could not load view activity.'); });
    return () => { cancelled = true; };
  }, [proposal.id, refresh]);

  async function share(native: boolean) {
    setNotice(''); setSharing(true);
    try {
      // Start native share in the gesture, but never send an outdated/unsaved proposal link.
      // Saving first can consume transient activation, so browsers without it fall back to copy.
      if (!(await flushPending())) { setNotice('Save your changes successfully before sharing.'); return; }
      if (native && navigator.share) {
        try { await navigator.share({ title: proposal.title, url }); return; }
        catch (error) { if (error instanceof DOMException && error.name === 'AbortError') return; }
      }
      await navigator.clipboard.writeText(url);
      setNotice('Link copied.');
    } catch { setNotice('Could not copy the link. Select and copy the URL below.'); }
    finally { setSharing(false); }
  }
  return <div className="space-y-5 proposal-manage">
    <SectionCard title="Publication & sharing" hint="Control what your client can see.">
      <p className="text-sm text-ink-soft">{live ? 'Your client link is live.' : 'Publish this proposal before sharing it with your client.'}</p>
      <LifecycleActions proposalId={proposal.id} slug={proposal.slug} status={proposal.status} onChanged={onChanged} flushPending={flushPending} />
    </SectionCard>
    <SectionCard title="Client link">
      <label className="block text-sm">Proposal URL<input className="security-input mt-2 w-full" readOnly value={url} onFocus={event => event.target.select()} /></label>
      <div className="flex flex-wrap gap-3">
        <button className="pill-outline" disabled={!live || sharing} onClick={() => void share(false)}>Copy link</button>
        <button className="pill-outline" disabled={!live || sharing} onClick={() => void share(true)}>Share…</button>
        {live && <a className="pill-outline" href={url} target="_blank" rel="noreferrer">View live</a>}
      </div>
      {notice && <p role="status" className="text-sm text-ink-soft">{notice}</p>}
    </SectionCard>
    <SectionCard title="View activity">
      {activityError ? <p role="alert">{activityError} <button className="pill-outline" onClick={() => setRefresh(value => value + 1)}>Retry</button></p>
        : activity ? <div className="text-sm text-ink-soft"><p>{activity.open_count} {activity.open_count === 1 ? 'view' : 'views'}</p>
          <p>{activity.last_viewed_at ? `Last viewed ${relativeTime(activity.last_viewed_at)}` : 'No client views yet.'}</p></div>
          : <p>Loading activity…</p>}
    </SectionCard>
    <SectionCard title="Version history" hint="Inspect a saved snapshot before restoring it.">
      <button className="pill-outline" onClick={onVersions}>Open version history</button>
    </SectionCard>
    {children}
  </div>;
}
